@if (pinnedStripOnly()) {
  <!-- Zero-clutter collapsed state: only show a sleek 1-line pinned marker strip if there are pinned notes and user hasn't hidden it -->
  @if (!isOpen() && pinnedNotes().length > 0 && !hidePinnedStrip()) {
    <div
      class="fluent-pinned-strip px-3 py-2 flex items-center justify-between gap-2"
      [class.fluent-pinned-strip--paper]="boardStyle() === 'paper'"
      [class.fluent-pinned-strip--board]="boardStyle() === 'board'"
      [ngClass]="'sticky-accent-' + (pinnedNotes()[0].color || 'amber')"
      animate.enter="hv-banner-enter"
      animate.leave="hv-banner-leave"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <lucide-icon name="Pin" size="14" class="text-primary shrink-0"></lucide-icon>
        @if (pinnedNotes()[0].stopNumber) {
          <span class="inline-flex items-center px-1.5 py-0.5 rounded bg-surface border border-border shrink-0">
            <app-text renderAs="span" size="xs" weight="semibold">Stop #{{ pinnedNotes()[0].stopNumber }}</app-text>
          </span>
        }
        <app-text size="xs" weight="medium" class="truncate text-foreground">
          {{ pinnedNotes()[0].content }}
        </app-text>
        @if (pinnedNotes().length > 1) {
          <span class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-surface-high border border-border shrink-0">
            <app-text renderAs="span" size="xs" color="muted">+{{ pinnedNotes().length - 1 }} pinned</app-text>
          </span>
        }
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <app-button variant="ghost" size="xs" icon="StickyNote" (click)="openBoard.emit()">
          Open
        </app-button>
        <app-button
          variant="ghost"
          size="xs"
          [iconOnly]="true"
          icon="X"
          title="Hide pinned reminder bar"
          (click)="hidePinnedStrip.set(true)"
        />
      </div>
    </div>
  }
} @else if (isOpen()) {
  <!-- Spatial Depth Scrim: softly blurs & recesses underlying cards so the 3D deck clearly floats above -->
  <div
    class="fluent-sticky-scrim"
    animate.enter="hv-backdrop-enter"
    animate.leave="hv-backdrop-leave"
    tabindex="-1"
    aria-hidden="true"
    (click)="closeBoard.emit()"
    (keydown.enter)="closeBoard.emit()"
  ></div>

  <!-- 3D Spatial Floating Sticky Scratchpad Stage (unfolds from active Sticky Notes trigger) -->
  <div
    class="fluent-sticky-stage"
    [class.fluent-sticky-stage--paper]="boardStyle() === 'paper'"
    [class.fluent-sticky-stage--board]="boardStyle() === 'board'"
    [class.fluent-sticky-stage--center]="caretAlign() === 'center'"
    [class.fluent-sticky-stage--left]="caretAlign() !== 'center'"
    [style.--sticky-max-h]="availableMaxHeightPx() ? availableMaxHeightPx() + 'px' : null"
    animate.enter="hv-sticky-3d-enter"
    animate.leave="hv-sticky-3d-leave"
  >
    <span
      class="fluent-sticky-caret"
      [class.fluent-sticky-caret--center]="caretAlign() === 'center'"
      [class.fluent-sticky-caret--left]="caretAlign() !== 'center'"
      aria-hidden="true"
    ></span>

    <div class="fluent-sticky-shell p-3.5 flex flex-col gap-3">
    <!-- Top Adhesive / Header Strip (fixed at top of popover) -->
    <div class="fluent-sticky-header-band -mx-3.5 -mt-3.5 px-3.5 pt-3 pb-2.5 flex items-center justify-between gap-2 flex-wrap shrink-0">
      <div class="flex items-center gap-2.5 min-w-0">
        <div class="w-8 h-8 rounded-xl bg-primary-low-contrast border border-border flex items-center justify-center shrink-0">
          <lucide-icon name="StickyNote" size="16" class="text-primary"></lucide-icon>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <app-text size="sm" weight="bold" class="text-foreground">Quick Sticky Notes</app-text>
            <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-surface border border-border">
              <app-text renderAs="span" size="xs" weight="semibold" class="text-primary">{{ notes().length }}</app-text>
            </span>
          </div>
          <app-text size="xs" color="muted" class="block">
            Internal scratchpad — never sent to brokers or drivers
          </app-text>
        </div>
      </div>

      <div class="flex items-center gap-1.5">
        @if (activeStopFilter() !== null) {
          <app-button
            variant="secondary"
            size="xs"
            shape="pill"
            icon="X"
            (click)="clearStopFilter.emit()"
            title="Clear stop filter"
          >
            Stop #{{ activeStopFilter() }}
          </app-button>
        }
        <app-button
          variant="secondary"
          size="xs"
          shape="pill"
          [icon]="boardStyle() === 'paper' ? 'Moon' : 'StickyNote'"
          (click)="toggleBoardStyle()"
          [title]="
            boardStyle() === 'paper'
              ? 'Switch to Midnight Indigo Board (night-shift friendly)'
              : 'Switch to Classic Paper Sticky Pad'
          "
        >
          {{ boardStyle() === 'paper' ? 'Midnight' : 'Paper' }}
        </app-button>
        <app-button
          [variant]="composerOpen() ? 'secondary' : 'primary'"
          size="xs"
          shape="pill"
          [icon]="composerOpen() ? 'ChevronUp' : 'Plus'"
          (click)="toggleComposer()"
        >
          {{ composerOpen() ? 'Hide Composer' : 'New Sticky' }}
        </app-button>
        <app-button
          variant="ghost"
          size="xs"
          [iconOnly]="true"
          icon="X"
          title="Collapse Sticky Notes (Esc)"
          (click)="closeBoard.emit()"
        />
      </div>
    </div>

    <!-- Segmented Filter Row (fixed at top of popover) -->
    <div class="flex items-center justify-between gap-2 flex-wrap shrink-0">
      <app-button-group
        size="sm"
        [options]="scopeFilterOptions()"
        [ngModel]="scopeFilter()"
        (ngModelChange)="scopeFilter.set($event)"
      />
      @if (personalCount() > 0 && scopeFilter() !== 'team') {
        <div class="inline-flex items-center gap-1 text-muted">
          <lucide-icon name="Lock" size="11" class="text-muted"></lucide-icon>
          <app-text renderAs="span" size="xs" color="muted">Personal notes are visible only to you</app-text>
        </div>
      }
    </div>

    <!-- Scrollable Body: Composer + Feed / Empty State -->
    <div class="fluent-sticky-scroll space-y-3 pr-1 pb-1">
      <!-- Quick Composer -->
      @if (composerOpen()) {
      <div
        class="fluent-sticky-card p-3 pl-4 flex flex-col gap-2.5"
        [ngClass]="'sticky-accent-' + draftColor()"
        animate.enter="hv-banner-enter"
        animate.leave="hv-banner-leave"
      >
        <div class="flex items-center justify-between gap-2 flex-wrap">
          <app-button-group
            size="sm"
            [options]="visibilityOptions"
            [ngModel]="draftVisibility()"
            (ngModelChange)="draftVisibility.set($event)"
          />

          <!-- Fluent Accent Swatches -->
          <div class="flex items-center gap-1">
            @for (swatch of accentOptions; track swatch.value) {
              <app-button
                variant="ghost"
                size="xs"
                shape="circle"
                [title]="swatch.label + ' sticky'"
                (click)="draftColor.set(swatch.value)"
              >
                <span
                  class="sticky-swatch-dot"
                  [ngClass]="[
                    'sticky-accent-' + swatch.value,
                    draftColor() === swatch.value ? 'sticky-swatch-selected' : ''
                  ]"
                ></span>
              </app-button>
            }
          </div>
        </div>

        <app-textarea
          [rows]="3"
          [autoGrow]="true"
          size="sm"
          [placeholder]="
            draftVisibility() === 'personal'
              ? 'Jot down a private reminder (Cmd/Ctrl + Enter to pin)...'
              : 'Write a shared team sticky note for everyone on this load...'
          "
          [ngModel]="draftContent()"
          (ngModelChange)="draftContent.set($event || '')"
          (keydown.control.enter)="submitDraft()"
          (keydown.meta.enter)="submitDraft()"
        />

        <div class="flex items-center justify-between gap-2 flex-wrap">
          <div class="flex items-center gap-2 flex-1 min-w-[180px]">
            <div class="flex-1 max-w-[230px]">
              <app-select
                size="sm"
                [sortOptions]="false"
                [options]="anchorOptions()"
                [ngModel]="draftStopAnchor()"
                (ngModelChange)="draftStopAnchor.set($event)"
                placeholder="Entire Load"
              />
            </div>
            <app-button
              [variant]="draftPinned() ? 'secondary' : 'ghost'"
              size="xs"
              [icon]="draftPinned() ? 'Pin' : 'PinOff'"
              (click)="draftPinned.set(!draftPinned())"
              title="Pin to top of load"
            >
              {{ draftPinned() ? 'Pinned' : 'Pin' }}
            </app-button>
          </div>

          <div class="flex items-center gap-1.5">
            <app-button variant="ghost" size="xs" (click)="composerOpen.set(false)" [disabled]="isSaving()">
              Cancel
            </app-button>
            <app-button
              variant="primary"
              size="xs"
              shape="pill"
              [icon]="draftVisibility() === 'personal' ? 'Lock' : 'Users'"
              [disabled]="!draftContent().trim() || isSaving()"
              [loading]="isSaving()"
              (click)="submitDraft()"
            >
              {{ draftVisibility() === 'personal' ? 'Save Personal' : 'Post to Team' }}
            </app-button>
          </div>
        </div>
      </div>
    }

    <!-- Sticky Notes Feed -->
    @if (filteredNotes().length > 0) {
      <div class="grid grid-cols-1 gap-2.5">
        @for (note of filteredNotes(); track note.id) {
          <div
            class="fluent-sticky-card p-3 pl-4 flex flex-col gap-2 group"
            [ngClass]="'sticky-accent-' + (note.color || 'amber')"
            animate.enter="hv-item-enter"
            animate.leave="hv-item-leave"
            [style.--stagger-idx]="$index"
          >
            <!-- Metadata Header -->
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-1.5 flex-wrap min-w-0">
                @if (note.pinned) {
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-high border border-border">
                    <lucide-icon name="Pin" size="10" class="text-primary"></lucide-icon>
                    <app-text renderAs="span" size="xs" weight="semibold" class="text-primary">Pinned</app-text>
                  </span>
                }

                @if (note.visibility === 'personal') {
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-border">
                    <lucide-icon name="Lock" size="10" class="text-muted"></lucide-icon>
                    <app-text renderAs="span" size="xs" weight="medium" color="muted">Personal</app-text>
                  </span>
                } @else {
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface border border-border">
                    <lucide-icon name="Users" size="10" class="text-primary"></lucide-icon>
                    <app-text renderAs="span" size="xs" weight="medium" class="text-primary">Shared</app-text>
                  </span>
                }

                @if (note.stopNumber) {
                  <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-high border border-border">
                    <lucide-icon name="MapPin" size="10" class="text-foreground"></lucide-icon>
                    <app-text renderAs="span" size="xs" weight="semibold">Stop #{{ note.stopNumber }}</app-text>
                  </span>
                }
              </div>

              <!-- Quick Actions -->
              <div class="flex items-center gap-0.5 shrink-0">
                @if (note.visibility === 'personal' && isOwnNote(note)) {
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="Share2"
                    title="Share this personal note with the whole team"
                    (click)="shareWithTeam(note)"
                  >
                    Share
                  </app-button>
                }
                @if (canModifyNote(note)) {
                  <app-button
                    variant="ghost"
                    size="xs"
                    [iconOnly]="true"
                    [icon]="note.pinned ? 'PinOff' : 'Pin'"
                    [title]="note.pinned ? 'Unpin sticky' : 'Pin sticky'"
                    (click)="togglePin(note)"
                  />
                  <app-button
                    variant="ghost"
                    size="xs"
                    [iconOnly]="true"
                    icon="Pencil"
                    title="Edit sticky note"
                    (click)="startEdit(note)"
                  />
                  <app-button
                    variant="ghost"
                    size="xs"
                    [iconOnly]="true"
                    icon="Trash2"
                    iconColor="text-danger"
                    title="Delete sticky note"
                    (click)="removeNote(note)"
                  />
                }
              </div>
            </div>

            <!-- Body / Inline Editor -->
            @if (editingNoteId() === note.id) {
              <div class="flex flex-col gap-2 pt-1">
                <app-textarea
                  [rows]="2"
                  [autoGrow]="true"
                  size="sm"
                  [ngModel]="editingContent()"
                  (ngModelChange)="editingContent.set($event || '')"
                />
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1">
                    @for (swatch of accentOptions; track swatch.value) {
                      <app-button
                        variant="ghost"
                        size="xs"
                        shape="circle"
                        [title]="swatch.label"
                        (click)="editingColor.set(swatch.value)"
                      >
                        <span
                          class="sticky-swatch-dot"
                          [ngClass]="[
                            'sticky-accent-' + swatch.value,
                            editingColor() === swatch.value ? 'sticky-swatch-selected' : ''
                          ]"
                        ></span>
                      </app-button>
                    }
                  </div>
                  <div class="flex items-center gap-1.5">
                    <app-button variant="ghost" size="xs" (click)="cancelEdit()">Cancel</app-button>
                    <app-button
                      variant="primary"
                      size="xs"
                      shape="pill"
                      icon="Check"
                      [disabled]="!editingContent().trim() || isSaving()"
                      (click)="saveEdit(note)"
                    >
                      Save
                    </app-button>
                  </div>
                </div>
              </div>
            } @else {
              <app-text size="sm" class="text-foreground whitespace-pre-wrap leading-relaxed break-words">
                {{ note.content }}
              </app-text>
            }

            <!-- Author & Timestamp Footer -->
            <div class="flex items-center justify-between gap-2 pt-1.5 border-t border-border">
              <app-text size="xs" color="muted" class="truncate">
                {{ isOwnNote(note) ? 'You' : note.authorName }}
                @if (formatNoteDate(note.createdAt); as dt) {
                  &middot; {{ dt | date: 'MMM d, h:mm a' }}
                }
              </app-text>
              @if (note.visibility === 'team' && isOwnNote(note)) {
                <app-button
                  variant="ghost"
                  size="xs"
                  icon="Lock"
                  title="Make private to only you"
                  (click)="makePersonal(note)"
                >
                  Make Private
                </app-button>
              }
            </div>
          </div>
        }
      </div>
    } @else if (!composerOpen()) {
      <!-- Clean Empty State -->
      <div class="py-5 px-4 rounded-xl bg-surface border border-dashed border-border flex flex-col items-center text-center gap-2">
        <lucide-icon name="Sparkles" size="18" class="text-primary"></lucide-icon>
        <app-text size="xs" color="muted">
          No {{ scopeFilter() === 'personal' ? 'personal ' : scopeFilter() === 'team' ? 'shared ' : '' }}sticky notes{{ activeStopFilter() !== null ? ' on Stop #' + activeStopFilter() : ' yet' }}.
          Jot quick call notes, dock codes, or reminders without cluttering broker instructions.
        </app-text>
        <app-button variant="secondary" size="xs" shape="pill" icon="Plus" (click)="composerOpen.set(true)">
          Add First Sticky
        </app-button>
      </div>
    }
    </div>
    </div>
  </div>
}
